import { useMemo } from 'react';
import { PostCreateDto, PostUpdateDto, PostRequestRecruiterDto } from '@libs/dto';
import { useLoading, useSnackbar, useConfirmation } from '@carcareers/contexts';
import { postApi } from '@carcareers/store';
import { isError, getErrorsAsString, getErrors } from '@carcareers/utils';
import { Post, PostRequest } from '@libs/models';
import { ApiError, ApiResponse } from '@libs/types';

interface PostCallbacks {
  onSuccess?: () => void;
}

export const usePost = () => {
  const { setLoading, setLabel } = useLoading();
  const { toggleSnackbar } = useSnackbar();
  const { confirm } = useConfirmation();
  const [postCreate] = postApi.usePostCreateMutation();
  const [postUpdateStartDate] = postApi.usePostUpdateStartDateMutation();
  const [postUpdateEndDate] = postApi.usePostUpdateEndDateMutation();
  const [postStart] = postApi.usePostStartMutation();
  const [postClose] = postApi.usePostCloseMutation();
  const [postDelete] = postApi.usePostDeleteMutation();
  const [postRequestRecruiter] = postApi.usePostRequestRecruiterMutation();
  const [postRenew] = postApi.usePostRenewMutation();

  return useMemo(
    () => ({
      createPost: async (post?: PostCreateDto, callbacks?: PostCallbacks): Promise<ApiResponse<Post>> => {
        if (!post) {
          toggleSnackbar('warning', 'Post data is empty.');
          return { data: undefined, errors: undefined };
        }

        setLabel('Creating Job Post...');
        setLoading(true);
        const response = await postCreate(post);
        let errors: ApiError[] = [];

        if (!isError(response)) {
          toggleSnackbar('success', 'Job Post created');
          if (callbacks?.onSuccess) {
            callbacks.onSuccess();
          }
        } else {
          errors = getErrors(response);
        }

        setLoading(false);

        return {
          errors,
          data: response.data,
        };
      },
      updateStartDate: async (post: PostUpdateDto, callbacks?: PostCallbacks): Promise<ApiResponse<Post>> => {
        if (!post.id) {
          toggleSnackbar('error', 'Post ID is required');
          return { data: undefined, errors: undefined };
        }

        setLabel('Updating Start Date...');
        setLoading(true);
        const response = await postUpdateStartDate(post);
        let errors: ApiError[] = [];

        if (!isError(response)) {
          toggleSnackbar('success', 'Start Date updated');
          if (callbacks?.onSuccess) {
            callbacks.onSuccess();
          }
        } else {
          errors = getErrors(response);
          toggleSnackbar('error', getErrorsAsString(response));
        }

        setLoading(false);

        return {
          errors,
          data: response.data,
        };
      },
      updateEndDate: async (post: PostUpdateDto, callbacks?: PostCallbacks): Promise<ApiResponse<Post>> => {
        if (!post.id) {
          toggleSnackbar('error', 'Post ID is required');
          return { data: undefined, errors: undefined };
        }

        setLabel('Updating End Date...');
        setLoading(true);
        const response = await postUpdateEndDate(post);
        let errors: ApiError[] = [];

        if (!isError(response)) {
          toggleSnackbar('success', 'End Date updated');
          if (callbacks?.onSuccess) {
            callbacks.onSuccess();
          }
        } else {
          errors = getErrors(response);
          toggleSnackbar('error', getErrorsAsString(response));
        }

        setLoading(false);

        return {
          errors,
          data: response.data,
        };
      },
      startPost: async (post: Post, callbacks?: PostCallbacks): Promise<ApiResponse<Post>> => {
        if (!post || !post.id) {
          toggleSnackbar('error', 'Post is required');
          return { data: undefined, errors: undefined };
        }

        setLabel('Starting Post...');
        setLoading(true);
        const response = await postStart(post.id);
        let errors: ApiError[] = [];

        if (!isError(response)) {
          toggleSnackbar('success', 'Post started');
          if (callbacks?.onSuccess) {
            callbacks.onSuccess();
          }
        } else {
          errors = getErrors(response);
          toggleSnackbar('error', getErrorsAsString(response));
        }

        setLoading(false);

        return {
          errors,
          data: response.data,
        };
      },
      closePost: async (post: Post, callbacks?: PostCallbacks): Promise<ApiResponse<Post>> => {
        if (!post || !post.id) {
          toggleSnackbar('error', 'Post is required');
          return { data: undefined, errors: undefined };
        }

        const confirmed = await confirm({
          title: 'Close Post',
          message:
            'Are you sure you want to close this post? Closing a post will stop all campaigns. This action cannot be undone. To reopen the post, you will need to create a new one.',
        });

        if (!confirmed) return { data: undefined, errors: undefined };

        setLabel('Closing Post...');
        setLoading(true);
        const response = await postClose(post.id);
        let errors: ApiError[] = [];

        if (!isError(response)) {
          toggleSnackbar('success', 'Post closed');
          if (callbacks?.onSuccess) {
            callbacks.onSuccess();
          }
        } else {
          errors = getErrors(response);
          toggleSnackbar('error', getErrorsAsString(response));
        }

        setLoading(false);

        return {
          errors,
          data: response.data,
        };
      },
      deletePost: async (id?: number, callbacks?: PostCallbacks): Promise<ApiResponse<Post>> => {
        if (!id) {
          toggleSnackbar('error', 'Post ID is required');
          return { data: undefined, errors: undefined };
        }

        const confirmed = await confirm({
          title: 'Delete Post',
          message: 'Are you sure you want to delete this post?',
        });

        if (!confirmed) return { data: undefined, errors: undefined };

        setLabel('Deleting post...');
        setLoading(true);
        const response = await postDelete(id);
        let errors: ApiError[] = [];

        if (!isError(response)) {
          toggleSnackbar('success', 'Post deleted');
          if (callbacks?.onSuccess) {
            callbacks.onSuccess();
          }
        } else {
          errors = getErrors(response);
          toggleSnackbar('error', getErrorsAsString(response));
        }

        setLoading(false);

        return {
          errors,
          data: response.data,
        };
      },
      requestRecruiter: async (
        post: PostRequestRecruiterDto,
        callbacks?: PostCallbacks,
      ): Promise<ApiResponse<PostRequest>> => {
        if (!post) {
          toggleSnackbar('warning', 'Post data is empty.');
          return { data: undefined, errors: undefined };
        }

        setLabel('Requesting Recruiter...');
        setLoading(true);
        const response = await postRequestRecruiter(post);
        let errors: ApiError[] = [];

        if (!isError(response)) {
          toggleSnackbar('success', 'Recruiter requested');
          if (callbacks?.onSuccess) {
            callbacks.onSuccess();
          }
        } else {
          errors = getErrors(response);
          toggleSnackbar('error', getErrorsAsString(response));
        }

        setLoading(false);

        return {
          errors,
          data: response.data,
        };
      },
      renewPost: async (post: Post, callbacks?: PostCallbacks): Promise<ApiResponse<Post>> => {
        if (!post || !post.id) {
          return { data: undefined, errors: undefined };
        }

        setLabel('Updating post...');
        setLoading(true);

        try {
          const response = await postRenew({ id: post.id, renew: post.renew ?? false });
          let errors: ApiError[] = [];

          if (!isError(response)) {
            toggleSnackbar('success', post.renew ? 'Auto-renewal enabled' : 'Auto-renewal disabled');

            if (callbacks?.onSuccess) {
              callbacks.onSuccess();
            }

            return {
              errors: [],
              data: response.data,
            };
          } else {
            errors = getErrors(response);
            toggleSnackbar('error', getErrorsAsString(response));

            return {
              errors,
              data: undefined,
            };
          }
        } finally {
          setLoading(false);
        }
      },
    }),
    [
      setLabel,
      setLoading,
      postCreate,
      toggleSnackbar,
      postUpdateStartDate,
      postUpdateEndDate,
      postStart,
      confirm,
      postClose,
      postDelete,
      postRequestRecruiter,
      postRenew,
    ],
  );
};
